import { ArcRotateCamera } from '@babylonjs/core/Cameras/arcRotateCamera';
import { Engine } from '@babylonjs/core/Engines/engine';
import { PointerEventTypes } from '@babylonjs/core/Events/pointerEvents';
import { HemisphericLight } from '@babylonjs/core/Lights/hemisphericLight';
import { ImportMeshAsync } from '@babylonjs/core/Loading/sceneLoader';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3, Color4 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Vector3 } from '@babylonjs/core/Maths/math.vector';
import { MeshoptCompression } from '@babylonjs/core/Meshes/Compression/meshoptCompression';
import type { AbstractMesh } from '@babylonjs/core/Meshes/abstractMesh';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Scene } from '@babylonjs/core/scene';
import '@babylonjs/core/Culling/ray';
import '@babylonjs/loaders/glTF/2.0';
import { MeshoptDecoder } from 'meshoptimizer';

/* The renderer core: one building from one GLB, drawn only when something changed (camera, highlight,
 * state), picking that answers with a place code. It knows nothing about the API, the app or the UI.
 *
 * Models carry no materials the viewer uses: colours are per vertex (scripts/convert-spot-model.ts), so
 * the glTF loader skips materials and Babylon's PBR code and shaders are never fetched or compiled. A
 * place is a volume (a room, a parking space: drawn only when tinted, see-through) or an object (a
 * desk: drawn as itself, recoloured when tinted). */

export type Tint = 'free' | 'booked' | 'selected' | 'neutral' | 'unknown';

const TINTS: Record<Tint, Color3> = {
  free: Color3.FromHexString('#3f9d6b'),
  booked: Color3.FromHexString('#c0504d'),
  selected: Color3.FromHexString('#2f6fb3'),
  neutral: Color3.FromHexString('#9aa7b0'),
  unknown: Color3.FromHexString('#c8c8c4'),
};
/** Tints that leave a place as the model draws it. */
const PLAIN: ReadonlySet<Tint> = new Set<Tint>(['neutral', 'unknown']);

// The decoder comes from the bundle, not from Babylon's CDN: Babylon only needs a script URL to load,
// so it gets an empty blob: one (a data: URL would need a weaker CSP) and finds the decoder on globalThis.
Object.assign(globalThis, { MeshoptDecoder });
MeshoptCompression.Configuration = {
  decoder: { url: URL.createObjectURL(new Blob([''], { type: 'text/javascript' })) },
};

export interface Viewer {
  load(url: string, nodeToPlace: Record<string, string>): Promise<void>;
  showFloor(floor: number | null): void;
  tint(code: string, tint: Tint): void;
  focus(code: string): void;
  /** Back to the whole building, from the side ('3d') or from above ('top'). */
  setView(mode: '3d' | 'top'): void;
  /** Name tags with a status dot over places (Booking pre DT 3D V1); an empty list removes them. */
  setLabels(labels: { code: string; text: string; status: 'free' | 'booked' }[]): void;
  onPick(listener: (code: string) => void): void;
  dispose(): void;
}

/** The floor number of the floor:<n> node a mesh sits under, or null outside any floor. */
const floorOf = (mesh: AbstractMesh): number | null => {
  let node: TransformNode | AbstractMesh | null = mesh;
  while (node && !/^floor:-?\d+$/.test(node.name)) node = node.parent as TransformNode | null;
  return node ? Number(node.name.slice('floor:'.length)) : null;
};

export function createViewer(canvas: HTMLCanvasElement): Viewer {
  const engine = new Engine(canvas, true, {
    preserveDrawingBuffer: false,
    stencil: false,
    adaptToDeviceRatio: true,
  });
  const scene = new Scene(engine);
  scene.clearColor = new Color4(0, 0, 0, 0);
  scene.skipPointerMovePicking = true;
  const camera = new ArcRotateCamera('camera', -Math.PI / 3, Math.PI / 3.2, 60, new Vector3(15, 3, 8), scene);
  camera.lowerRadiusLimit = 8;
  camera.upperRadiusLimit = 150;
  camera.upperBetaLimit = Math.PI / 2.05;
  camera.attachControl(canvas, true);
  const light = new HemisphericLight('light', new Vector3(0.3, 1, 0.2), scene);
  light.intensity = 0.95;
  light.groundColor = new Color3(0.35, 0.35, 0.38);
  if (import.meta.env.DEV) Object.assign(window, { budovanScene: scene });
  // What every untinted mesh is drawn with: its vertex colours, lit, from both sides (models are
  // exported double-sided).
  const plain = scene.defaultMaterial as StandardMaterial;
  plain.backFaceCulling = false;
  plain.specularColor = Color3.Black();

  const materials = new Map<string, StandardMaterial>();
  const material = (tint: Tint, volume: boolean): StandardMaterial => {
    const key = `${tint}:${volume ? 'volume' : 'object'}`;
    let found = materials.get(key);
    if (!found) {
      found = new StandardMaterial(`tint:${key}`, scene);
      found.diffuseColor = TINTS[tint];
      found.specularColor = Color3.Black();
      found.backFaceCulling = false;
      // A volume is a box round a room: see-through when tinted, invisible (but still tappable) when not.
      if (volume) found.alpha = PLAIN.has(tint) ? 0 : 0.5;
      found.freeze();
      materials.set(key, found);
    }
    return found;
  };

  const placeOfNode = new Map<string, string>();
  const meshesOfPlace = new Map<string, AbstractMesh[]>();
  const volumes = new Set<string>();
  const floors = new Map<number, TransformNode>();
  // The whole-building framing from load, for setView.
  let home = { target: camera.target.clone(), radius: camera.radius };
  const SIDE_ALPHA = -Math.PI / 3;
  const SIDE_BETA = Math.PI / 3.2;
  const FOCUS_BETA = Math.PI / 4;
  const TOP_BETA = 0.01;
  // setTarget alone keeps the camera where it is and turns it; aim keeps the angles.
  const aim = (target: Vector3, alpha: number, beta: number): void => {
    camera.setTarget(target);
    camera.alpha = alpha;
    camera.beta = beta;
  };

  // Labels are DOM over the canvas, moved after each frame (frames only happen when something moved).
  const labelLayer = document.createElement('div');
  labelLayer.className = 'labels';
  canvas.after(labelLayer);
  let labels: { mesh: AbstractMesh; element: HTMLElement; floor: number | null }[] = [];
  // The highest floor drawn: tags show only there, so a tag never floats over the floors above its place.
  let topFloor: number | null = null;
  scene.onAfterRenderObservable.add(() => {
    if (labels.length === 0) return;
    const scale = engine.getHardwareScalingLevel();
    const viewport = camera.viewport.toGlobal(engine.getRenderWidth(), engine.getRenderHeight());
    const transform = scene.getTransformMatrix();
    // Nearest first; a tag that would overlap one already placed is left out until the view changes.
    const placed: { x: number; y: number; w: number; h: number }[] = [];
    const projected = labels
      .map(({ mesh, element, floor }) => ({
        mesh,
        element,
        floor,
        at: Vector3.Project(
          mesh.getBoundingInfo().boundingBox.centerWorld,
          Matrix.IdentityReadOnly,
          transform,
          viewport,
        ),
      }))
      .toSorted((a, b) => a.at.z - b.at.z);
    for (const { mesh, element, at, floor } of projected) {
      const x = at.x * scale;
      const y = at.y * scale;
      const box = {
        x: x - element.offsetWidth / 2,
        y: y - element.offsetHeight / 2,
        w: element.offsetWidth,
        h: element.offsetHeight,
      };
      const free = !placed.some(
        (other) =>
          box.x < other.x + other.w &&
          other.x < box.x + box.w &&
          box.y < other.y + other.h &&
          other.y < box.y + box.h,
      );
      const onTop = topFloor === null || floor === null || floor === topFloor;
      const shown = onTop && mesh.isEnabled() && at.z > 0 && at.z < 1 && free;
      element.style.visibility = shown ? 'visible' : 'hidden';
      if (!shown) continue;
      placed.push(box);
      element.style.transform = `translate(${x}px, ${y}px) translate(-50%, -50%)`;
    }
  });
  const listeners: ((code: string) => void)[] = [];

  // Render on demand: a frame only when the camera moved or something was marked dirty.
  let dirty = true;
  const markDirty = (): void => {
    dirty = true;
  };
  /* The active mesh list is frozen (no per-frame culling work), but only once every material is ready:
   * frozen while a shader still compiles, a mesh drops out of the list and stays invisible. A batch of
   * changes (a floor, a repaint of every place) re-freezes once. */
  let refreezing = false;
  const refreeze = (): void => {
    scene.unfreezeActiveMeshes();
    markDirty();
    if (refreezing) return;
    refreezing = true;
    scene.executeWhenReady(() => {
      refreezing = false;
      scene.freezeActiveMeshes();
      markDirty();
    });
  };

  camera.onViewMatrixChangedObservable.add(markDirty);
  engine.runRenderLoop(() => {
    if (!dirty) return;
    dirty = false;
    scene.render();
    // How many frames have been drawn. tests/e2e/tests/twin reads it to see that the scene is drawn on
    // load and again after a change, without a screenshot; it is a DOM attribute, so it works in builds.
    canvas.dataset.frames = String(Number(canvas.dataset.frames ?? 0) + 1);
  });
  const resize = (): void => {
    engine.resize();
    markDirty();
  };
  window.addEventListener('resize', resize);

  scene.onPointerObservable.add((info) => {
    if (info.type !== PointerEventTypes.POINTERTAP) return;
    let node: TransformNode | AbstractMesh | null = info.pickInfo?.pickedMesh ?? null;
    while (node && !placeOfNode.has(node.name)) node = node.parent as TransformNode | null;
    const code = node ? placeOfNode.get(node.name) : undefined;
    if (code) for (const listener of listeners) listener(code);
  });

  const highestPlaceFloor = (): number | null => {
    const withPlaces = [...meshesOfPlace.values()]
      .map((meshes) => (meshes[0] ? floorOf(meshes[0]) : null))
      .filter((floor): floor is number => floor !== null);
    if (withPlaces.length > 0) return Math.max(...withPlaces);
    return floors.size > 0 ? Math.max(...floors.keys()) : null;
  };

  return {
    async load(url, nodeToPlace) {
      const result = await ImportMeshAsync(url, scene, {
        pluginExtension: '.glb',
        pluginOptions: { gltf: { skipMaterials: true } },
      });
      for (const [node, code] of Object.entries(nodeToPlace)) placeOfNode.set(node, code);
      for (const transform of scene.transformNodes) {
        const match = /^floor:(-?\d+)$/.exec(transform.name);
        if (match) floors.set(Number(match[1]), transform);
      }
      for (const mesh of result.meshes) {
        mesh.isPickable = false;
        mesh.freezeWorldMatrix();
        let node: TransformNode | AbstractMesh | null = mesh;
        while (node && !placeOfNode.has(node.name)) node = node.parent as TransformNode | null;
        const code = node ? placeOfNode.get(node.name) : undefined;
        if (!code || !node) continue;
        if ((node.metadata?.gltf?.extras as { volume?: boolean } | undefined)?.volume) volumes.add(code);
        mesh.isPickable = true;
        mesh.material = volumes.has(code) ? material('neutral', true) : null;
        meshesOfPlace.set(code, [...(meshesOfPlace.get(code) ?? []), mesh]);
      }
      // Frame the building, not the street round it, whatever the model's own origin and handedness.
      const framed = floors.size > 0 ? [...floors.values()] : result.meshes.slice(0, 1);
      if (framed.length > 0) {
        const min = new Vector3(Infinity, Infinity, Infinity);
        const max = new Vector3(-Infinity, -Infinity, -Infinity);
        for (const node of framed) {
          const bounds = node.getHierarchyBoundingVectors(true);
          min.minimizeInPlace(bounds.min);
          max.maximizeInPlace(bounds.max);
        }
        aim(Vector3.Center(min, max), SIDE_ALPHA, SIDE_BETA);
        // A portrait phone screen is narrow: back off until the building fits across it.
        const portrait = engine.getRenderHeight() / Math.max(1, engine.getRenderWidth());
        camera.radius = Vector3.Distance(min, max) * 1.1 * Math.max(1, portrait * 0.75);
        camera.upperRadiusLimit = camera.radius * 2;
        home = { target: camera.target.clone(), radius: camera.radius };
      }
      // Geometry stays on the GPU only; the CPU copy is not needed after upload. Places keep theirs:
      // picking a tap and framing a focused place read it, and they are a few triangles each.
      for (const mesh of result.meshes) if (!mesh.isPickable) mesh.geometry?.clearCachedData();
      // With nothing cut, labels show on the highest floor that has places, not on the plain floors
      // above it (the Spot model's top floors are building only).
      topFloor = highestPlaceFloor();
      refreeze();
    },
    showFloor(floor) {
      for (const [number, node] of floors) {
        node.setEnabled(floor === null || number <= floor);
        // The cut floor shows its inside: its own facade (floor:<n>:facade in the model) goes.
        for (const child of node.getChildMeshes(true))
          if (child.name.startsWith(`${node.name}:facade`)) child.setEnabled(number !== floor);
      }
      // A floor of a real building is tens of metres across: bring the whole floor into view.
      const group = floor === null ? undefined : floors.get(floor);
      if (group) {
        const { min, max } = group.getHierarchyBoundingVectors(true);
        const centre = Vector3.Center(min, max);
        aim(new Vector3(centre.x, max.y, centre.z), camera.alpha, FOCUS_BETA);
        camera.radius = Math.hypot(max.x - min.x, max.z - min.z) * 0.9;
      }
      topFloor = floor ?? highestPlaceFloor();
      refreeze();
    },
    tint(code, tint) {
      const meshes = meshesOfPlace.get(code) ?? [];
      const volume = volumes.has(code);
      for (const mesh of meshes) {
        const untinted = !volume && PLAIN.has(tint);
        mesh.material = untinted ? null : material(tint, volume);
        // A tinted object shows the tint itself, not the tint times its own colour.
        if (!volume) mesh.useVertexColors = untinted;
      }
      if (meshes.length > 0) refreeze();
    },
    focus(code) {
      const meshes = meshesOfPlace.get(code);
      const first = meshes?.[0];
      if (!first) return;
      // Cut the building at the place's floor so the floors above do not hide it.
      let node: TransformNode | AbstractMesh | null = first;
      while (node && !/^floor:-?\d+$/.test(node.name)) node = node.parent as TransformNode | null;
      if (node) this.showFloor(Number(node.name.slice('floor:'.length)));
      first.refreshBoundingInfo({});
      const box = first.getBoundingInfo().boundingBox;
      // From above at an angle, into the floor the cut has opened.
      aim(box.centerWorld.clone(), camera.alpha, FOCUS_BETA);
      // Close enough to pick out the place, far enough to see what is around it.
      camera.radius = Math.max(15, Vector3.Distance(box.minimumWorld, box.maximumWorld) * 4);
      markDirty();
    },
    setView(mode) {
      aim(home.target.clone(), SIDE_ALPHA, mode === 'top' ? TOP_BETA : SIDE_BETA);
      camera.radius = home.radius;
      markDirty();
    },
    setLabels(next) {
      labelLayer.replaceChildren();
      labels = next.flatMap(({ code, text, status }) => {
        const mesh = meshesOfPlace.get(code)?.[0];
        if (!mesh) return [];
        const element = document.createElement('span');
        element.className = `label ${status}`;
        element.textContent = text;
        element.style.visibility = 'hidden';
        labelLayer.append(element);
        return [{ mesh, element, floor: floorOf(mesh) }];
      });
      markDirty();
    },
    onPick(listener) {
      listeners.push(listener);
    },
    dispose() {
      window.removeEventListener('resize', resize);
      engine.dispose();
    },
  };
}
